/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import {
    defaultSortId,
    resolveSortOptions,
    selectSortOption,
    type ResolvedSortOption,
    type SortOption,
} from '@/lib/search/sort-options';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

/**
 * The sort control's shared plumbing: resolve the option list, work out which
 * one is selected, and write a choice to the URL.
 *
 * WHY THIS EXISTS AS A HOOK. Four controls on this surface already carry
 * byte-identical copies of this block — `sort-select.tsx`, `sort-menu.tsx`,
 * `sticky-bar.tsx` and `bg-filter-sort-bar.tsx`. Each copy has independently
 * had to learn the same two lessons the hard way, recorded on
 * {@link defaultSortId} and {@link selectSortOption}: the selected option is
 * NOT `options[0]` (both brands' live lists end on their default rather than
 * starting on it), and an id the current brand's list does not contain must
 * miss rather than fall through to position 0. The two mobile overlays are new
 * call sites, so they take the shared copy rather than becoming copies five and
 * six. The four existing controls are deliberately NOT refactored onto it here
 * — that is a behaviour-preserving change to shipped controls and belongs in
 * its own pass, not inside a parity fix.
 *
 * OPTIMISTIC SELECTION. While a navigation is pending the in-flight target's
 * `?sort=` is read instead of the committed location's, so the control paints
 * the sort the shopper just chose rather than the one still on screen.
 */
export interface SortSelection {
    /** The resolved list, server-provided where BM has one, curated otherwise. */
    options: ResolvedSortOption[];
    /** The option the current URL selects, or the list's own default. */
    selected: ResolvedSortOption | undefined;
    /** Index of {@link selected}; 0 when nothing matched. */
    selectedIndex: number;
    /** Render label for an option — server label, else translated key. */
    labelOf: (option: ResolvedSortOption) => string;
    /** Write a sort id to the URL and reset paging. */
    choose: (id: string) => void;
    /** True while a sort navigation is in flight. */
    isPending: boolean;
}

export function useSortSelection(
    serverOptions: Array<{ id?: string; label?: string }> | undefined,
    /** The brand's curated list — named by the caller, never defaulted. */
    fallbackOptions: readonly SortOption[]
): SortSelection {
    const { t } = useTranslation('search');
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();

    const options = resolveSortOptions(serverOptions, fallbackOptions);

    const currentSort =
        (navigation.location
            ? new URLSearchParams(navigation.location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.SORT)
            : new URLSearchParams(location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.SORT)) ?? defaultSortId(options);

    const selected = selectSortOption(options, currentSort);
    const selectedIndex = Math.max(
        0,
        options.findIndex((option) => option.id === selected?.id)
    );

    const labelOf = useCallback(
        (option: ResolvedSortOption) =>
            option.label ??
            (option.labelKey ? t(option.labelKey, { defaultValue: option.defaultLabel ?? option.id }) : option.id),
        [t]
    );

    const choose = useCallback(
        (id: string) => {
            const params = new URLSearchParams(location.search);
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.SORT, id);
            // Reset paging — both the legacy offset and the crawlable page param.
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, '0');
            params.delete('page');
            void navigate({ ...location, search: `?${params.toString()}` });
        },
        [location, navigate]
    );

    return {
        options,
        selected,
        selectedIndex,
        labelOf,
        choose,
        isPending: navigation.state !== 'idle',
    };
}
